import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import SearchBar from './SearchBar';
import TimeBillingEntryModal from './TimeBillingEntryModal';
import { caseDbApiEndpoint } from '../utils/apiOrigin';
import '../styles/BillingSetup.css';
import '../styles/TimeBilling.css';

const endpointFor = (apiBase) => {
    const normalizedBase = String(apiBase || '').trim().replace(/\/+$/, '');
    return normalizedBase ? `${normalizedBase}/my-time-billing.php` : caseDbApiEndpoint('my-time-billing.php');
};

export const formatMyTimeHours = (value) => {
    const hours = Number(value);
    if (!Number.isFinite(hours)) return '—';
    return hours.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatMyTimeMoney = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';
    return amount.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

export const appendUniqueTimeEntries = (current = [], incoming = []) => {
    const byId = new Map((Array.isArray(current) ? current : []).map(entry => [Number(entry?.id), entry]));
    (Array.isArray(incoming) ? incoming : []).forEach(entry => {
        const id = Number(entry?.id);
        if (Number.isInteger(id) && id > 0) byId.set(id, entry);
    });
    return Array.from(byId.values());
};

const entryStatus = (entry) => {
    if (Number(entry?.draft) === 1) return 'Draft';
    if (Number(entry?.time_invoice_id || entry?.invoice_id) > 0 || Number(entry?.status) === 2) return 'Billed';
    return 'Unbilled';
};

const MyTimeBilling = ({ apiBase = '', navVariant = 'compact' }) => {
    const endpoint = endpointFor(apiBase);
    const [entries, setEntries] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, per_page: 50, total: 0, total_pages: 1, has_more: false });
    const [query, setQuery] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [showEntryModal, setShowEntryModal] = useState(false);
    const requestGenerationRef = useRef(0);

    const loadEntries = useCallback(async ({ page = 1, append = false, q = appliedQuery } = {}) => {
        const generation = ++requestGenerationRef.current;
        append ? setLoadingMore(true) : setLoading(true);
        setError('');
        const params = new URLSearchParams({
            action: 'list',
            page: String(page),
            per_page: '50',
        });
        if (String(q || '').trim()) params.set('q', String(q).trim());

        try {
            const response = await fetch(`${endpoint}?${params.toString()}`, { credentials: 'include' });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload?.success !== true) {
                throw new Error(payload?.message || `Your time entries could not be loaded (${response.status}).`);
            }
            if (generation !== requestGenerationRef.current) return;
            const nextEntries = Array.isArray(payload.entries) ? payload.entries : [];
            setEntries(current => append ? appendUniqueTimeEntries(current, nextEntries) : nextEntries);
            setPagination({
                page: Number(payload.pagination?.page || page),
                per_page: Number(payload.pagination?.per_page || 50),
                total: Number(payload.pagination?.total || 0),
                total_pages: Number(payload.pagination?.total_pages || 1),
                has_more: payload.pagination?.has_more === true,
            });
        } catch (loadError) {
            if (generation === requestGenerationRef.current) {
                setError(loadError.message || 'Your time entries could not be loaded.');
            }
        } finally {
            if (generation === requestGenerationRef.current) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [appliedQuery, endpoint]);

    useEffect(() => {
        loadEntries({ page: 1, append: false, q: appliedQuery });
    }, [appliedQuery, loadEntries]);

    useEffect(() => {
        const timer = setTimeout(() => setAppliedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const totalHours = useMemo(() => entries.reduce((sum, entry) => {
        const value = Number(entry?.hours ?? entry?.raw_quantity);
        return sum + (Number.isFinite(value) ? value : 0);
    }, 0), [entries]);

    return (
        <main className={`page-container my-time-page ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div id="page-header">
                <div className="my-time-header-copy">
                    <h2>Time &amp; Billing</h2>
                    <p className="subtext">Review your time across cases and add new entries.</p>
                </div>
                <button type="button" className="action" onClick={() => setShowEntryModal(true)}>
                    <Plus size={17} /> Add Time
                </button>
            </div>

            <div className="case-filters my-time-toolbar" role="search" aria-label="Search time entries">
                <SearchBar
                    expanded
                    value={query}
                    setSearchQuery={setQuery}
                    placeholder="Search cases or descriptions"
                    ariaLabel="Search my time"
                />
                <button
                    type="button"
                    className="action tertiary"
                    onClick={() => loadEntries({ page: 1, append: false })}
                    disabled={loading}
                >
                    <RefreshCw size={15} className={loading ? 'spinner' : ''} /> Refresh
                </button>
            </div>

            <div className="my-time-content">
                {error && <div className="my-time-error" role="alert">{error}</div>}
                <div className="my-time-summary subtext" role="status" aria-label="Time summary">
                    {loading ? 'Loading your time…' : (
                        <>
                            <span>Showing {entries.length} of {pagination.total} entries</span>
                            <span>{formatMyTimeHours(totalHours)} hours shown</span>
                        </>
                    )}
                </div>
                <div className="billing-table-scroll my-time-table-scroll" role="region" aria-label="Time entries" tabIndex={0}>
                    <table className="billing-table table-full default my-time-table" aria-label="My time entries" aria-busy={loading}>
                        <thead>
                            <tr className="default">
                                <th scope="col">Date</th>
                                <th scope="col">Case</th>
                                <th scope="col">Description</th>
                                <th scope="col">Hours</th>
                                <th scope="col">Rate</th>
                                <th scope="col">Total</th>
                                <th scope="col">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading || entries.length === 0 ? (
                                <tr className="default billing-empty-row">
                                    <td colSpan={7} className="my-time-empty">
                                        {loading ? 'Loading your time…' : error ? 'Time entries could not be loaded.' : appliedQuery ? 'No time entries match your search.' : 'No time entries yet. Add time to get started.'}
                                    </td>
                                </tr>
                            ) : entries.map(entry => (
                                <tr className="default" key={entry.id}>
                                    <td>{String(entry.date || '').slice(0, 10) || '—'}</td>
                                    <td>
                                        <Link to={`/case/${entry.case_id}`}>{entry.case_name || `Case #${entry.case_id}`}</Link>
                                    </td>
                                    <td className="my-time-description">{entry.description || '—'}</td>
                                    <td>{formatMyTimeHours(entry.hours ?? entry.raw_quantity)}</td>
                                    <td>{formatMyTimeMoney(entry.rate)}</td>
                                    <td>{formatMyTimeMoney(entry.total)}</td>
                                    <td><span className="tag">{entryStatus(entry)}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {pagination.has_more && !loading && (
                    <div className="my-time-load-more">
                        <button
                            type="button"
                            className="action tertiary"
                            onClick={() => loadEntries({ page: pagination.page + 1, append: true })}
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Loading…' : 'Load more'}
                        </button>
                    </div>
                )}
            </div>

            {showEntryModal && (
                <TimeBillingEntryModal
                    apiBase={apiBase}
                    onClose={() => setShowEntryModal(false)}
                    onCreated={() => loadEntries({ page: 1, append: false })}
                />
            )}
        </main>
    );
};

export default MyTimeBilling;
